import { ChangeEvent, useCallback } from "react";

import CheckboxField from "@/components/molecules/forms/CheckboxField";

interface CheckBoxItem<TValue extends string | number> {
  id: string;
  label: string | JSX.Element | undefined;
  value: TValue;
  disabled?: boolean;
}

type ItemRenderer<
  TValue extends string | number,
  TItem extends CheckBoxItem<TValue>,
> = (
  children: JSX.Element,
  item: TItem,
  onItemChange: (ev: ChangeEvent<HTMLInputElement>) => void,
) => JSX.Element;

type ElementRenderer = <
  TValue extends string | number,
  TItem extends CheckBoxItem<TValue>,
>(
  item: TItem,
  values: TValue[],
  groupName: string,
  onChange: (e: ChangeEvent<HTMLInputElement>) => void,
  containerClassName?: string,
) => JSX.Element;
const checkboxElementRenderer: ElementRenderer = (
  item,
  values,
  groupName,
  onChange,
  containerClassName,
) => {
  return (
    <CheckboxField
      id={item.id}
      key={`${groupName}-${item.id}`}
      name={groupName}
      label={item.label}
      value={item.value}
      checked={values.includes(item.value)}
      disabled={!!item.disabled}
      onChange={onChange}
      containerClassName={containerClassName}
    />
  );
};

interface Props<
  TValue extends string | number,
  TItem extends CheckBoxItem<TValue>,
> {
  items: TItem[];
  groupName: string;

  values: TValue[];
  onChange?: (values: TValue[]) => void;

  itemRenderer?: ItemRenderer<TValue, TItem>;
  containerClassName?: string;
}

export function CheckboxGroup<
  TValue extends string | number,
  TItem extends CheckBoxItem<TValue>,
>({
  items,
  groupName,
  values,
  onChange,
  itemRenderer,
  containerClassName,
}: Props<TValue, TItem>) {
  const defaultItemRenderer: ItemRenderer<TValue, TItem> = (children) => {
    return children;
  };
  const itemRendererWithDefault = itemRenderer ?? defaultItemRenderer;
  const onItemChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      if (!onChange || !ev.target.value) {
        return;
      }
      const castedValue =
        typeof items[0]?.value === "number"
          ? (Number(ev.target.value) as TValue)
          : (ev.target.value as TValue);

      const newValues = values.includes(castedValue)
        ? values.filter((v) => v !== castedValue)
        : [...values, castedValue];

      onChange(newValues);
    },
    [items, values, onChange],
  );
  return (
    <>
      {items.map((item) =>
        itemRendererWithDefault(
          checkboxElementRenderer(
            item,
            values,
            groupName,
            onItemChange,
            containerClassName,
          ),
          item,
          onItemChange,
        ),
      )}
    </>
  );
}
